"use client";

import { LogIn, LogOut, User } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { getApiBase, getCurrentUser, loginAccount, registerAccount } from "@/lib/api";
import type { AuthUser } from "@/lib/api";

type AuthMode = "login" | "register";

const TOKEN_KEY = "app_buddy_auth_token";

function saveToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

function readToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function AuthButton() {
  const locale = useLocale();
  const t = useTranslations("Auth");
  const [mode, setMode] = useState<AuthMode>("login");
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    setIsMounted(true);

    const bootstrap = async () => {
      const params = new URLSearchParams(window.location.search);
      const tokenFromOauth = params.get("auth_token");

      if (tokenFromOauth) {
        saveToken(tokenFromOauth);
        params.delete("auth_token");
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
        window.history.replaceState({}, "", nextUrl);
      }

      const token = readToken();
      if (!token) {
        return;
      }

      try {
        const user = await getCurrentUser(token);
        setCurrentUser(user);
      } catch {
        clearToken();
      }
    };

    void bootstrap();
  }, []);

  const title = useMemo(() => (mode === "login" ? t("loginTitle") : t("registerTitle")), [mode, t]);

  const openOauthLogin = () => {
    const nextPath = `/${locale}`;
    const target = `${getApiBase()}/auth/google/login?next=${encodeURIComponent(nextPath)}`;
    window.location.href = target;
  };

  const resetForm = () => {
    setIdentifier("");
    setPassword("");
    setUsername("");
    setEmail("");
    setError(null);
  };

  const signOut = () => {
    clearToken();
    setCurrentUser(null);
    setIsOpen(false);
    resetForm();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result =
        mode === "login"
          ? await loginAccount({ identifier, password })
          : await registerAccount({ username, email, password });

      saveToken(result.access_token);
      setCurrentUser(result.user);
      setIsOpen(false);
      resetForm();
    } catch (err) {
      const message = err instanceof Error ? err.message : t("genericError");
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {currentUser ? (
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-1 rounded-full border border-zinc-300 bg-zinc-100/90 px-3 py-1.5 text-xs text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-300 sm:inline-flex">
            <User className="h-3.5 w-3.5" />
            <span>{currentUser.username}</span>
          </div>
          <button
            type="button"
            onClick={signOut}
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 bg-zinc-100/90 px-3 text-xs text-zinc-700 transition hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-200 dark:hover:border-zinc-500"
          >
            <LogOut className="h-3.5 w-3.5" />
            {t("signOut")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 bg-zinc-100/90 px-3 text-xs font-medium text-zinc-700 transition hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-200 dark:hover:border-zinc-500"
        >
          <LogIn className="h-3.5 w-3.5" />
          {t("signIn")}
        </button>
      )}

      {isMounted && isOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[9999] grid place-items-center bg-black/75 p-4 backdrop-blur-sm"
              role="dialog"
              aria-modal="true"
              aria-label={title}
            >
              <div className="w-full max-w-md rounded-2xl border border-zinc-300 bg-white p-5 shadow-[0_28px_90px_rgba(0,0,0,0.45)] dark:border-zinc-700 dark:bg-zinc-950">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-full px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-900"
              >
                {t("close")}
              </button>
            </div>

            <div className="mb-4 inline-flex rounded-full border border-zinc-300 p-1 dark:border-zinc-700">
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`rounded-full px-3 py-1 text-xs ${
                  mode === "login"
                    ? "bg-zinc-200 text-zinc-900 dark:bg-zinc-700 dark:text-zinc-100"
                    : "text-zinc-600 dark:text-zinc-300"
                }`}
              >
                {t("loginTab")}
              </button>
              <button
                type="button"
                onClick={() => setMode("register")}
                className={`rounded-full px-3 py-1 text-xs ${
                  mode === "register"
                    ? "bg-zinc-200 text-zinc-900 dark:bg-zinc-700 dark:text-zinc-100"
                    : "text-zinc-600 dark:text-zinc-300"
                }`}
              >
                {t("registerTab")}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === "register" ? (
                <input
                  type="text"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder={t("username")}
                  className="w-full rounded-xl border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 outline-none ring-zinc-500 focus:ring dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                  required
                />
              ) : null}

              <input
                type={mode === "register" ? "email" : "text"}
                value={mode === "register" ? email : identifier}
                onChange={(event) => {
                  if (mode === "register") {
                    setEmail(event.target.value);
                  } else {
                    setIdentifier(event.target.value);
                  }
                }}
                placeholder={mode === "register" ? t("email") : t("identifier")}
                className="w-full rounded-xl border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 outline-none ring-zinc-500 focus:ring dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                required
              />

              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={t("password")}
                className="w-full rounded-xl border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 outline-none ring-zinc-500 focus:ring dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                required
                minLength={8}
              />

              {error ? <p className="text-xs text-rose-500">{error}</p> : null}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-xl bg-zinc-900 px-3 py-2 text-sm font-medium text-zinc-100 transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
              >
                {isSubmitting ? t("submitting") : mode === "login" ? t("loginAction") : t("registerAction")}
              </button>
            </form>

            <div className="my-4 h-px bg-zinc-200 dark:bg-zinc-800" />

            <button
              type="button"
              onClick={openOauthLogin}
              className="w-full rounded-xl border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm font-medium text-zinc-800 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
            >
              {t("continueGoogle")}
            </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
